/**
 * Мост «бюджет → поле» — план сезона на ОДНО поле.
 *
 * `GET /api/v1/fields/{id}/season-plan?season_id=…` (backend `field_card.py`).
 * Ручка только читает: она показывает, что холдинг уже запланировал на это
 * поле — культуру, урожайность, назначенную техкарту и её операции с
 * агроокнами. Ничего не создаёт и бюджет не меняет.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface PlanVariantRef {
  id: string;
  name: string;
  status: string;
  /** Утверждён как план сезона — та самая «производственная программа». */
  is_approved: boolean;
  is_primary: boolean;
  is_selected: boolean;
}

export interface PlanTechCard {
  id: string;
  name: string;
  status: string | null;
  operations_count: number;
  target_yield_c_ha: number | null;
  cost_per_ha: number | null;
  cost_total: number | null;
}

export interface PlannedOperation {
  operation_id: string;
  seq: number | null;
  name: string;
  block_kind: string | null;
  block_label: string | null;
  window_starts_on: string | null;
  window_ends_on: string | null;
  area_ha: number | null;
  area_share_pct: number | null;
  volume: number | null;
  volume_basis: string | null;
  is_wip: boolean;
  cost_per_ha: number | null;
  cost_total: number | null;
}

export interface PlannedMaterial {
  budget_item_id: string | null;
  name: string | null;
  section: string | null;
  unit_id: string | null;
  qty: number | null;
  amount: number | null;
  operations: string[];
  gap: string | null;
}

export interface SowPrefill {
  crop_id: string | null;
  local_catalog_item_id: string | null;
  crop_name: string | null;
  area_ha: number | null;
  subzone_id: string | null;
  window_starts_on: string | null;
  window_ends_on: string | null;
  seeding_density: number | null;
  seeding_density_unit: string | null;
  seeding_norm: number | null;
  seeding_norm_unit: string | null;
  variety_id: string | null;
  variety_name: string | null;
}

export interface FieldSeasonPlan {
  field_id: string;
  field_name: string | null;
  /** Хозяйство поля — им адресуется вариант плана в ссылке `?farm=`. */
  farm_id: string | null;
  season_id: string;
  season_year: number | null;
  has_plan: boolean;
  /** Почему плана нет — словами. Пустота без объяснения читается как поломка. */
  empty_reason: string | null;
  variant: PlanVariantRef | null;
  sowing_id: string | null;
  crop_id: string | null;
  crop_name: string | null;
  /** Сорт/гибрид по плану — с семенной позиции строки сева техкарты. */
  variety_id: string | null;
  variety_name: string | null;
  is_fallow: boolean;
  area_ha: number | null;
  planned_yield_c_ha: number | null;
  gross_t: number | null;
  predecessor_crop_id: string | null;
  predecessor_label: string | null;
  other_plan_sowings: number;
  tech_card: PlanTechCard | null;
  tech_card_gap: string | null;
  operations: PlannedOperation[];
  materials: PlannedMaterial[];
  with_money: boolean;
  sow_prefill: SowPrefill | null;
}

export const seasonPlanKeys = {
  ofField: (fieldId: string, seasonId: string | null) =>
    ['field-card', 'season-plan', fieldId, seasonId] as const,
};

export function useFieldSeasonPlan(
  fieldId: string,
  seasonId: string | null,
): UseQueryResult<FieldSeasonPlan, Error> {
  return useQuery<FieldSeasonPlan, Error>({
    queryKey: seasonPlanKeys.ofField(fieldId, seasonId),
    enabled: Boolean(fieldId && seasonId),
    staleTime: 60_000,
    queryFn: () =>
      api.get<FieldSeasonPlan>(
        `/api/v1/fields/${fieldId}/season-plan?season_id=${seasonId}`,
      ),
  });
}
